import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'

const bosses = [
  {
    name: 'Procrastinazione',
    desc: 'Ruba tempo. Ti fa rimandare anche ciò che conta.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
    color: '#8b5cf6',
  },
  {
    name: 'Stress cronico',
    desc: 'Consuma energia e rende tutto più pesante.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
    color: '#ef4444',
  },
  {
    name: 'Dopamina distrutta',
    desc: 'Ti tiene attaccato a stimoli rapidi e piaceri vuoti.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
        <path d="M8 12h8M12 8v8" />
      </svg>
    ),
    color: '#f59e0b',
  },
  {
    name: 'Ansia sociale',
    desc: 'Ti isola anche quando vorresti esserci.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
    color: '#67e8f9',
  },
  {
    name: 'Zero energia',
    desc: 'Non è pigrizia. Spesso è sistema nervoso saturo.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <path d="M18.36 6.64a9 9 0 11-12.73 0" />
        <path d="M12 2v10" />
      </svg>
    ),
    color: '#6b7280',
  },
  {
    name: 'Safe zone',
    desc: 'Sembra rifugio. Poi diventa prigione.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0110 0v4" />
      </svg>
    ),
    color: '#10b981',
  },
]

function BossCard({ boss, index }) {
  const cardRef = useRef(null)
  const isInView = useInView(cardRef, { once: true, margin: '-50px' })

  const handleMouseMove = (e) => {
    const card = cardRef.current
    if (!card) return
    const rect = card.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const centerX = rect.width / 2
    const centerY = rect.height / 2
    const rotateX = (y - centerY) / 15
    const rotateY = (centerX - x) / 15
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`
  }

  const handleMouseLeave = () => {
    const card = cardRef.current
    if (!card) return
    card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)'
  }

  return (
    <motion.div
      ref={cardRef}
      className="nerd-card rounded-xl p-6 cursor-pointer relative overflow-hidden group"
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ transition: 'transform 0.15s ease-out, border-color 0.3s ease, box-shadow 0.3s ease' }}
    >
      {/* Glow border on hover */}
      <div
        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          boxShadow: `inset 0 0 20px ${boss.color}20, 0 0 30px ${boss.color}15`,
        }}
      />

      <div className="relative z-10">
        <div
          className="w-14 h-14 rounded-lg flex items-center justify-center mb-4"
          style={{ color: boss.color, background: `${boss.color}15` }}
        >
          {boss.icon}
        </div>

        <h3 className="font-display text-lg font-semibold text-nerd-white mb-2">
          {boss.name}
        </h3>

        <p className="text-nerd-gray text-sm leading-relaxed">
          {boss.desc}
        </p>

        {/* Mini stat bar */}
        <div className="mt-4 flex items-center gap-2">
          <span className="text-[10px] text-nerd-gray uppercase tracking-wider font-display">Livello</span>
          <div className="flex-1 h-1 bg-nerd-dark rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${60 + Math.random() * 30}%`,
                background: `linear-gradient(90deg, ${boss.color}80, ${boss.color})`,
              }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default function BossSection() {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })

  return (
    <section
      id="boss"
      ref={sectionRef}
      className="relative w-full py-24 md:py-32 px-4"
      style={{ background: '#050510' }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-nerd-white mb-4">
            Ogni player ha i suoi{' '}
            <span className="text-gradient">boss</span>.
          </h2>
          <p className="text-nerd-gray text-base md:text-lg max-w-xl mx-auto">
            Il punto non è evitarli. È imparare come affrontarli.
          </p>
        </motion.div>

        {/* Boss grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {bosses.map((boss, i) => (
            <BossCard key={boss.name} boss={boss} index={i} />
          ))}
        </div>
      </div>
    </section>
  )
}
